import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {Alert, Button, CircularProgress, Snackbar} from '@mui/material';
import {useLocation} from 'react-router-dom';
import {apiErrorMessage} from '../utilities/apiError';
type Feedback = { message: string, severity?: 'success' | 'error' | 'info', undo?: () => Promise<unknown> };
const Context = createContext<(feedback: Feedback) => void>(() => undefined);
export const useTaskFeedback = () => useContext(Context);

// Lives above task lists so a completion message and Undo remain available after a row moves or collapses.
export function TaskFeedbackProvider({children}: {children: React.ReactNode}) {
    const [feedback, setFeedback] = useState<(Feedback & {id: number}) | null>(null);
    const [busy, setBusy] = useState(false);
    const {pathname} = useLocation();
    useEffect(() => {setFeedback(null);}, [pathname]);
    const show = useCallback((value: Feedback) => { setFeedback({...value, id: Date.now()}); }, []);
    const undo = async () => {
        if (!feedback?.undo || busy) return;
        const current = feedback;
        setBusy(true);
        try {
            await current.undo();
            setFeedback((held) => held?.id === current.id ? {message: 'Change undone', id: Date.now(), severity: 'info'} : held);
        } catch (error) {
            setFeedback((held) => held?.id === current.id ? {...current, message: apiErrorMessage(error, 'Could not undo. Please try again.'), severity: 'error'} : held);
        } finally { setBusy(false); }
    };
    return <Context.Provider value={show}>
        {children}
        <Snackbar key={feedback?.id} open={!!feedback} autoHideDuration={busy || feedback?.severity === 'error' ? null : feedback?.undo ? 12000 : 5000}
            onClose={(_event, reason) => { if (reason !== 'clickaway' && !busy) setFeedback(null); }}
            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
            <Alert severity={feedback?.severity ?? 'success'} variant="filled" onClose={busy ? undefined : () => setFeedback(null)}
                sx={{alignItems: 'center', maxWidth: 'min(620px, calc(100vw - 32px))'}}
                action={feedback?.undo ? <Button color="inherit" disabled={busy} onClick={() => void undo()} startIcon={busy ? <CircularProgress size={14} color="inherit"/> : undefined}>Undo</Button> : undefined}>
                {feedback?.message}
            </Alert>
        </Snackbar>
    </Context.Provider>;
}
